import Controller, { OnCancel, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { FragmentNames, WoundPictureEditAliases } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { HttpService, HttpStatus } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { Inject } from '@odinjs/odin';
import { FormAPI, PhotoUploaderAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { FileUploadResponse, FormWoundImage, WoundImageEntity } from './wounds-images-interface';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import DialogService from '../../../../services/dialog-service';
import moment from 'moment';
import { WoundsDressingsTabListContext } from '../../service/wounds-dressing-tab-list-service';
import { WoundsDressingTabType } from '../../types/wounds-dressings-types';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';

@Controller({ fragment: FragmentNames.WOUND_PICTURE_EDIT })
class WoundsImagesEditController extends BaseController {

  @Inject(WoundPictureEditAliases.PHOTO_UPLOADER)
  private photoUploaderAPI!: PhotoUploaderAPI;

  @Inject(WoundPictureEditAliases.FORM_ADD_PICTURE)
  private formAddPictureAPI!:FormAPI;

  @OnMount(WoundPictureEditAliases.WOUND_PICTURE_LAYOUT)
  handleMountLayout(): void {
    const { encounterId, patientId, woundId } = this.historyService.getCurrentRouteParams();

    this.context.set({
      encounterId,
      patientId,
      woundId
    });
  }

  @OnCancel(WoundPictureEditAliases.FORM_ADD_PICTURE)
  handleCancelForm(): void {
    this.goBack();
  }

  @OnSubmit(WoundPictureEditAliases.FORM_ADD_PICTURE)
  handleSubmitForm(event: Record<string, Value>): void {
    this.uploadImageToStorage(event);
    return;
  }

  private uploadImageToStorage(event: Record<string, Value>): Promise<string> {
    const formData = new FormData();
    const blob = this.photoUploaderAPI.getValue();

    if (blob == undefined) return Promise.reject(new Error('No image to upload'));

    formData.append('file', blob);

    return HttpService.post(Endpoint.POST_UPLOAD_WOUND_IMAGE, undefined, { body: formData })
      .then(response => {
        const { path } = response.body as FileUploadResponse;
        if (path.length > 255) {
          this.showToast('text.file-name-length-exceeded', ToastLevel.ERROR);
          this.formAddPictureAPI.cancel();
          return '';
        }
        this.saveWoundImage(path, event);
        return path;
      });
  }

  private saveWoundImage(path: string, event: Record<string, Value>): void {
    const bodyWoundImage = this.getBodyWoundImage(path, event);
    HttpService.post(Endpoint.POST_SAVE_WOUND_IMAGE, undefined, { body: bodyWoundImage })
      .then(response => {
        const body = response.body as WoundImageEntity;
        const status = response.status;
        if (status === HttpStatus.OK) {
          this.showModalSubmit(body.woundImageId);
        }
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formAddPictureAPI.setLoading(false));
    return;
  }

  private getBodyWoundImage(path: string, event: Record<string, Value>): Record<string, Value> {
    const formData = event.record as FormWoundImage;
    const imageTime = formData.imageTime ? DateFormatterUtil.timefieldToLocalTimezone(String(formData.imageTime)).split(':') : null;
    const imageDateTime = imageTime ? moment(formData.imageDate).hour(parseInt(imageTime[0])).minute(parseInt(imageTime[1])) : moment().format(ISO_FULL_DATE_FORMAT);
    const { woundId } = this.historyService.getCurrentRouteParams() as {woundId: number};
    this.formAddPictureAPI.setLoading(true);
    return {
      filePath: path,
      observation: formData.observation,
      pictureDate: imageDateTime,
      title: formData.title,
      woundSequence: woundId
    };
  }

  private releaseWoundImage(woundImageId: number): void {
    HttpService.put(Endpoint.PUT_RELEASE_WOUND_IMAGE, { woundImageId })
      .then(() => {
        this.goBack();
      }).catch((err) => {
        if (err?.body) {
          this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
        }
      }).finally(() => {
        this.formAddPictureAPI.setLoading(false);
      });
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => {
        applicationContext.popNavigationItem();
        applicationContext.popNavigationItem();
        WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.IMAGES;
      },
      onClickOk: () => { this.releaseWoundImage(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    WoundsDressingsTabListContext.currentTab = WoundsDressingTabType.IMAGES;
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'wounds-image-release',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default WoundsImagesEditController;
